import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  forgotPassword,
  googleSignIn,
  login,
  logout,
  requestMagicLink,
  resendVerification,
  resetPassword,
  signup,
  verifyEmail,
  verifyMagicLink,
} from "@/services/auth";
import { AUTH_SESSION_QUERY_KEY } from "@/lib/authContextValue";
import type {
  AuthSession,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  GoogleSignInRequest,
  LoginRequest,
  LogoutResponse,
  MagicLinkRequest,
  MagicLinkRequestResponse,
  MagicLinkVerifyRequest,
  ResendVerificationResponse,
  ResetPasswordRequest,
  SignupRequest,
  VerifyEmailRequest,
  VerifyEmailResponse,
} from "@/types";

/**
 * Mutation hooks for the /api/v1/auth/* surface.
 *
 * Source: ENG-AUTH-LOGIN-SIGNUP frontend half.
 *
 * Each hook invalidates the ``["auth", "me"]`` query on a successful
 * mutation so the AuthProvider re-fetches the session and the protected
 * routes flip from "loading"/"unauthenticated" to "authenticated"
 * without the caller having to refetch by hand.
 */

export function useSignup() {
  const queryClient = useQueryClient();
  return useMutation<AuthSession, Error, SignupRequest>({
    mutationFn: signup,
    onSuccess: (session) => {
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
    },
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  return useMutation<AuthSession, Error, LoginRequest>({
    mutationFn: login,
    onSuccess: (session) => {
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation<LogoutResponse, Error, void>({
    mutationFn: logout,
    onSuccess: () => {
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, null);
      // Drop every cached query — workspace-bound caches are invalid
      // for the next user.
      queryClient.clear();
    },
  });
}

export function useForgotPassword() {
  return useMutation<ForgotPasswordResponse, Error, ForgotPasswordRequest>({
    mutationFn: forgotPassword,
  });
}

export function useResetPassword() {
  const queryClient = useQueryClient();
  return useMutation<AuthSession, Error, ResetPasswordRequest>({
    mutationFn: resetPassword,
    onSuccess: (session) => {
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
    },
  });
}

export function useVerifyEmail() {
  const queryClient = useQueryClient();
  return useMutation<VerifyEmailResponse, Error, VerifyEmailRequest>({
    mutationFn: verifyEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AUTH_SESSION_QUERY_KEY });
    },
  });
}

export function useResendVerification() {
  return useMutation<ResendVerificationResponse, Error, void>({
    mutationFn: resendVerification,
  });
}

export function useRequestMagicLink() {
  return useMutation<MagicLinkRequestResponse, Error, MagicLinkRequest>({
    mutationFn: requestMagicLink,
  });
}

export function useVerifyMagicLink() {
  const queryClient = useQueryClient();
  return useMutation<AuthSession, Error, MagicLinkVerifyRequest>({
    mutationFn: verifyMagicLink,
    onSuccess: (session) => {
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
    },
  });
}

export function useGoogleSignIn() {
  const queryClient = useQueryClient();
  return useMutation<AuthSession, Error, GoogleSignInRequest>({
    mutationFn: googleSignIn,
    onSuccess: (session) => {
      queryClient.setQueryData(AUTH_SESSION_QUERY_KEY, session);
    },
  });
}
